import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LayoutSettings } from "../../../../boxes/LayoutSettings/LayoutSettings";
import { EntityHeader } from "../../../../widgets/EntityHeader/EntityHeader";
import { Box } from "../../../../shared/Box/Box";
import { http } from "../../../../services/HttpService";
import { HTTP_ENDPOINTS } from "../../../../consts/api";
import { uiNotification } from "../../../../services/UINotificationService";
import styles from "./wellsync.module.scss";
import { CircularProgress } from "../../../../shared/CircularProgress/CircularProgress";

const emptyForm = () => ({
  clinicId: "",
  wellsyncClinicId: "",
  clinicApiKey: "",
  signingKey: "",
  pharmacyPrivateKeyPem: "",
  isEnabled: true,
});

function sameClinicId(a, b) {
  return String(a) === String(b);
}

/** Resolve display name from clinics list loaded for this account. */
function getClinicDisplayName(clinicId, clinicsList) {
  const row = clinicsList.find((c) => sameClinicId(c.id, clinicId));
  if (!row) return "";
  const name = (row.clinic_name || row.name || "").trim();
  return name || `Clinic ${row.id}`;
}

export const WellSync = () => {
  const [configs, setConfigs] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, clinRes] = await Promise.all([
        http.get(HTTP_ENDPOINTS.wellsync.configs()),
        http.get(HTTP_ENDPOINTS.getClinics()),
      ]);
      const cfgData = cfgRes.data && cfgRes.data.data;
      setConfigs(Array.isArray(cfgData) ? cfgData : []);
      const raw = clinRes.data && clinRes.data.data;
      let list;
      if (Array.isArray(raw)) {
        list = raw;
      } else {
        const inner = raw && raw.data;
        list = inner != null ? inner : raw != null ? raw : [];
      }
      setClinics(Array.isArray(list) ? list : []);
    } catch (e) {
      uiNotification.error("Failed to load WellSync settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const clinicOptions = useMemo(() => {
    const configured = new Set(configs.map((c) => Number(c.clinicId)));
    return clinics.filter((c) => c.id != null && !configured.has(Number(c.id)));
  }, [clinics, configs]);

  const editingConfig = useMemo(
    () => configs.find((x) => String(x.id) === String(editingId)) || null,
    [configs, editingId],
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.clinicId || !form.wellsyncClinicId) {
      uiNotification.error("Clinic and WellSync clinic id are required");
      return;
    }
    setSaving(true);
    try {
      await http.post(HTTP_ENDPOINTS.wellsync.configs(), {
        clinicId: Number(form.clinicId),
        wellsyncClinicId: form.wellsyncClinicId.trim(),
        clinicApiKey: form.clinicApiKey.trim(),
        signingKey: form.signingKey.trim(),
        pharmacyPrivateKeyPem: form.pharmacyPrivateKeyPem.trim() || undefined,
        isEnabled: form.isEnabled,
      });
      uiNotification.success("WellSync configuration saved");
      setForm(emptyForm());
      await load();
    } catch (err) {
      uiNotification.error(
        err.response?.data?.message || "Failed to save configuration",
      );
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditForm({
      clinicId: c.clinicId,
      wellsyncClinicId: c.wellsyncClinicId,
      clinicApiKey: "",
      signingKey: "",
      pharmacyPrivateKeyPem: "",
      isEnabled: c.isEnabled,
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingId) return;
    setSaving(true);
    try {
      const payload = {
        wellsyncClinicId: editForm.wellsyncClinicId.trim(),
        isEnabled: editForm.isEnabled,
      };
      if (editForm.clinicApiKey.trim()) {
        payload.clinicApiKey = editForm.clinicApiKey.trim();
      }
      if (editForm.signingKey.trim()) {
        payload.signingKey = editForm.signingKey.trim();
      }
      if (editForm.pharmacyPrivateKeyPem.trim()) {
        payload.pharmacyPrivateKeyPem = editForm.pharmacyPrivateKeyPem.trim();
      }
      await http.put(`wellsync/configs/${editingId}`, payload);
      uiNotification.success("WellSync configuration updated");
      setEditingId(null);
      await load();
    } catch (err) {
      uiNotification.error(
        err.response?.data?.message || "Failed to update configuration",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove WellSync configuration for this clinic?")) {
      return;
    }
    setSaving(true);
    try {
      await http.delete(`wellsync/configs/${id}`);
      uiNotification.success("Configuration removed");
      await load();
    } catch (err) {
      uiNotification.error("Failed to delete configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (c) => {
    const nextEnabled = !c.isEnabled;
    const clinicLabel =
      getClinicDisplayName(c.clinicId, clinics) || `Clinic ${c.clinicId}`;
    const confirmMessage = nextEnabled
      ? `Enable WellSync for "${clinicLabel}"? Staff will be able to use the WellSync prescription flow for this clinic.`
      : `Disable WellSync for "${clinicLabel}"? The WellSync catalog will no longer be available for this clinic.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }
    setSaving(true);
    try {
      await http.post(`wellsync/configs/${c.id}/toggle`, {
        isEnabled: nextEnabled,
      });
      await load();
    } catch (err) {
      uiNotification.error("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <LayoutSettings contentClassName={styles.root}>
      <EntityHeader title="WellSync (CareConnect)" />
      <Box>
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <p>
              Connect each clinic using credentials from the WellSync portal.
              API keys are stored encrypted on the server and are not returned
              to the browser after saving.
            </p>
            <p>
              Optional <strong>pharmacy.js RSA private key</strong> (PKCS8 PEM)
              enables the embedded WellSync pharmacy widget when{" "}
              <code>REACT_APP_WELLSYNC_USE_PHARMACY_WIDGET=true</code> is set at
              build time. See{" "}
              <a
                href="https://github.com/wellsync/pharmacy.js"
                target="_blank"
                rel="noopener noreferrer"
              >
                wellsync/pharmacy.js
              </a>
              .
            </p>

            <div className={styles.card}>
              <h3>Add clinic</h3>
              <form onSubmit={handleCreate}>
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label>Clinic</label>
                    <select
                      className={styles.input}
                      value={form.clinicId}
                      onChange={(e) => {
                        const clinicId = e.target.value;
                        setForm((f) => ({ ...f, clinicId }));
                      }}
                      required
                    >
                      <option value="">Select clinic</option>
                      {clinicOptions.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.clinic_name || c.name || c.id}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className={styles.stack}>
                  <div className={styles.field}>
                    <label>WellSync clinic id</label>
                    <input
                      className={styles.input}
                      value={form.wellsyncClinicId}
                      onChange={(e) => {
                        const wellsyncClinicId = e.target.value;
                        setForm((f) => ({
                          ...f,
                          wellsyncClinicId,
                        }));
                      }}
                      required
                    />
                  </div>
                  <div className={styles.field}>
                    <label>Clinic API key</label>
                    <input
                      className={styles.input}
                      type="password"
                      autoComplete="new-password"
                      value={form.clinicApiKey}
                      onChange={(e) => {
                        const clinicApiKey = e.target.value;
                        setForm((f) => ({ ...f, clinicApiKey }));
                      }}
                      required
                    />
                  </div>
                  <div className={styles.field}>
                    <label>Signing key</label>
                    <input
                      className={styles.input}
                      type="password"
                      autoComplete="new-password"
                      value={form.signingKey}
                      onChange={(e) => {
                        const signingKey = e.target.value;
                        setForm((f) => ({ ...f, signingKey }));
                      }}
                      required
                    />
                  </div>
                  <div className={styles.field}>
                    <label>Pharmacy.js private key (PEM, optional)</label>
                    <textarea
                      className={styles.input}
                      rows={4}
                      autoComplete="off"
                      placeholder="-----BEGIN PRIVATE KEY-----"
                      value={form.pharmacyPrivateKeyPem}
                      onChange={(e) => {
                        const pharmacyPrivateKeyPem = e.target.value;
                        setForm((f) => ({ ...f, pharmacyPrivateKeyPem }));
                      }}
                    />
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label>
                      <input
                        type="checkbox"
                        checked={form.isEnabled}
                        onChange={(e) => {
                          const isEnabled = e.target.checked;
                          setForm((f) => ({ ...f, isEnabled }));
                        }}
                      />{" "}
                      Enabled
                    </label>
                  </div>
                </div>
                <button
                  type="submit"
                  className={styles.btnPrimary}
                  disabled={saving}
                >
                  Save
                </button>
              </form>
            </div>

            <h3>Configured clinics</h3>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Clinic name</th>
                  <th>WellSync clinic id</th>
                  <th>Enabled</th>
                  <th>Keys</th>
                  <th>Pharmacy.js</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {configs.map((c) => (
                  <tr key={c.id}>
                    <td>
                      {getClinicDisplayName(c.clinicId, clinics) ||
                        `Clinic ${c.clinicId}`}
                    </td>
                    <td>{c.wellsyncClinicId}</td>
                    <td>{c.isEnabled ? "Yes" : "No"}</td>
                    <td>
                      {c.hasClinicApiKey ? "API ✓" : "API —"} /{" "}
                      {c.hasSigningKey ? "Signing ✓" : "Signing —"}
                    </td>
                    <td>{c.hasPharmacyPrivateKeyPem ? "PEM ✓" : "—"}</td>
                    <td className={styles.actions}>
                      <button
                        type="button"
                        className={`${styles.btnToggle} ${
                          c.isEnabled
                            ? styles.btnToggle_on
                            : styles.btnToggle_off
                        }`}
                        onClick={() => handleToggle(c)}
                        disabled={saving}
                        title={
                          c.isEnabled
                            ? "Click to disable WellSync for this clinic"
                            : "Click to enable WellSync for this clinic"
                        }
                      >
                        {c.isEnabled ? "Disable" : "Enable"}
                      </button>
                      <button
                        type="button"
                        className={styles.btnGhost}
                        onClick={() => startEdit(c)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className={styles.btnDanger}
                        onClick={() => handleDelete(c.id)}
                        disabled={saving}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {editingId && editingConfig && (
              <div className={styles.card}>
                <h3>Edit configuration</h3>
                <p className={styles.editLead}>
                  Clinic:{" "}
                  <strong>
                    {getClinicDisplayName(editingConfig.clinicId, clinics) ||
                      `Clinic ${editingConfig.clinicId}`}
                  </strong>
                </p>
                <form onSubmit={handleUpdate}>
                  <div className={styles.stack}>
                    <div className={styles.field}>
                      <label>WellSync clinic id</label>
                      <input
                        className={styles.input}
                        value={editForm.wellsyncClinicId}
                        onChange={(e) => {
                          const wellsyncClinicId = e.target.value;
                          setEditForm((f) => ({
                            ...f,
                            wellsyncClinicId,
                          }));
                        }}
                        required
                      />
                    </div>
                    <div className={styles.field}>
                      <label>Clinic API key</label>
                      <span className={styles.hint}>
                        {editingConfig.hasClinicApiKey
                          ? "Current key is saved on the server (not shown). Leave blank to keep it, or enter a new key to replace."
                          : "No key on file. Enter a key to add one."}
                      </span>
                      <input
                        className={styles.input}
                        type="password"
                        autoComplete="new-password"
                        value={editForm.clinicApiKey}
                        placeholder={
                          editingConfig.hasClinicApiKey
                            ? "Leave blank to keep existing"
                            : "Enter clinic API key"
                        }
                        onChange={(e) => {
                          const clinicApiKey = e.target.value;
                          setEditForm((f) => ({
                            ...f,
                            clinicApiKey,
                          }));
                        }}
                      />
                    </div>
                    <div className={styles.field}>
                      <label>Signing key</label>
                      <span className={styles.hint}>
                        {editingConfig.hasSigningKey
                          ? "Current key is saved on the server (not shown). Leave blank to keep it, or enter a new key to replace."
                          : "No key on file. Enter a key to add one."}
                      </span>
                      <input
                        className={styles.input}
                        type="password"
                        autoComplete="new-password"
                        value={editForm.signingKey}
                        placeholder={
                          editingConfig.hasSigningKey
                            ? "Leave blank to keep existing"
                            : "Enter signing key"
                        }
                        onChange={(e) => {
                          const signingKey = e.target.value;
                          setEditForm((f) => ({
                            ...f,
                            signingKey,
                          }));
                        }}
                      />
                    </div>
                    <div className={styles.field}>
                      <label>Pharmacy.js private key (PEM, optional)</label>
                      <span className={styles.hint}>
                        {editingConfig.hasPharmacyPrivateKeyPem
                          ? "A PEM is saved (not shown). Paste a new PEM to replace, or leave blank to keep the existing key."
                          : "Paste PKCS8 PEM to enable the embedded pharmacy.js flow when the app is built with REACT_APP_WELLSYNC_USE_PHARMACY_WIDGET=true."}
                      </span>
                      <textarea
                        className={styles.input}
                        rows={4}
                        autoComplete="off"
                        placeholder={
                          editingConfig.hasPharmacyPrivateKeyPem
                            ? "Leave blank to keep existing PEM"
                            : "-----BEGIN PRIVATE KEY-----"
                        }
                        value={editForm.pharmacyPrivateKeyPem}
                        onChange={(e) => {
                          const pharmacyPrivateKeyPem = e.target.value;
                          setEditForm((f) => ({
                            ...f,
                            pharmacyPrivateKeyPem,
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={styles.field}>
                      <label>
                        <input
                          type="checkbox"
                          checked={editForm.isEnabled}
                          onChange={(e) => {
                            const isEnabled = e.target.checked;
                            setEditForm((f) => ({
                              ...f,
                              isEnabled,
                            }));
                          }}
                        />{" "}
                        Enabled
                      </label>
                    </div>
                  </div>
                  <div className={styles.actions}>
                    <button
                      type="submit"
                      className={styles.btnPrimary}
                      disabled={saving}
                    >
                      Update
                    </button>
                    <button
                      type="button"
                      className={styles.btnGhost}
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}
      </Box>
    </LayoutSettings>
  );
};

export default WellSync;
